import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import api from "utils/api";
import {
  getStoredPreferredLanguage,
  normalizePreferredLanguage,
  resolvePreferredLanguage,
  setStoredPreferredLanguage,
} from "utils/preferredLanguage";
import { setUser } from "state";
import { isLeonardoFlowPath } from "utils/leonardoFlow";
import {
  createTranslator,
  DEFAULT_LEONARDO_LOCALE,
  getDateLocale,
  getGallery,
  getHotelOptions,
} from "./i18n";

const LeonardoLocaleContext = createContext(null);

export const LeonardoLocaleProvider = ({ children }) => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const [locale, setLocaleState] = useState(() => resolvePreferredLanguage(user));
  const [updating, setUpdating] = useState(false);

  const isLeonardoPage = location.pathname.startsWith("/leonardo-nyx");
  const isHomePage = location.pathname === "/";
  const isLeonardoFlow = isLeonardoFlowPath(location.pathname, location.search);

  useEffect(() => {
    setLocaleState(resolvePreferredLanguage(user));
  }, [user]);

  useEffect(() => {
    if (!user?._id || !isLeonardoFlow) return undefined;

    const stored = getStoredPreferredLanguage();
    const account = normalizePreferredLanguage(user.preferredLanguage);
    if (stored === account) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.patch("/auth/language", {
          preferredLanguage: stored,
        });
        if (!cancelled && data?.user) {
          dispatch(setUser({ user: data.user }));
        }
      } catch {
        /* keep local UI locale */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, isLeonardoFlow, user]);

  const setLocale = useCallback(
    async (next) => {
      const normalized = normalizePreferredLanguage(next);
      setLocaleState(normalized);
      setStoredPreferredLanguage(normalized);

      if (!user?._id) return;

      setUpdating(true);
      try {
        const { data } = await api.patch("/auth/language", {
          preferredLanguage: normalized,
        });
        if (data?.user) dispatch(setUser({ user: data.user }));
      } catch {
        setLocaleState(resolvePreferredLanguage(user));
      } finally {
        setUpdating(false);
      }
    },
    [dispatch, user]
  );

  const t = useMemo(
    () => createTranslator(locale || DEFAULT_LEONARDO_LOCALE),
    [locale]
  );
  const dateLocale = useMemo(() => getDateLocale(locale), [locale]);
  const hotelOptions = useMemo(() => getHotelOptions(t), [t]);
  const shellT = useMemo(
    () => (isLeonardoFlow && !isHomePage ? t : createTranslator(DEFAULT_LEONARDO_LOCALE)),
    [isHomePage, isLeonardoFlow, t]
  );

  useEffect(() => {
    if (!isLeonardoFlow) return undefined;
    const previous = document.documentElement.lang;
    document.documentElement.lang = locale === "en" ? "en" : "de";
    return () => {
      document.documentElement.lang = previous;
    };
  }, [isLeonardoFlow, locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      updating,
      t,
      shellT,
      dateLocale,
      hotelOptions,
      galleryLabels: getGallery(t),
      isLeonardoPage,
      isLeonardoFlow,
    }),
    [
      locale,
      setLocale,
      updating,
      t,
      shellT,
      dateLocale,
      hotelOptions,
      isLeonardoPage,
      isLeonardoFlow,
    ]
  );

  return (
    <LeonardoLocaleContext.Provider value={value}>
      {children}
    </LeonardoLocaleContext.Provider>
  );
};

export const useLeonardoLocale = () => {
  const ctx = useContext(LeonardoLocaleContext);
  if (!ctx) {
    throw new Error("useLeonardoLocale must be used within LeonardoLocaleProvider");
  }
  return ctx;
};
